import classnames from 'clsx';

import { Text, TextType } from 'ui-components/components/text';

import style from './settings-page-header.module.css';
import { SettingsPageTitle } from '../settings-page-title/settings-page-title';

import type { PrimitiveType } from 'intl-messageformat';
import type React from 'react';
import type { MessageDescriptor } from 'react-intl';

type SettingsPageWrapperHeaderProps = {
  children: React.ReactNode;
  isTabbed: boolean;
  hasAdditionalContent: boolean;
};

type MessageWithValues = MessageDescriptor & { values?: Record<string, PrimitiveType> };

/**
 * Messages object containing the header and optional description for a settings page.
 * - `headerMessage`: Required. The main title displayed at the top of the page.
 * - `descriptionMessage`: Optional. Descriptive text shown below the header.
 */
export type SettingsPageHeaderMessages = {
  headerMessage: MessageWithValues;
  descriptionMessage?: MessageWithValues;
};

type SettingsPageHeaderProps = {
  messages: SettingsPageHeaderMessages;
  descriptionContent?: React.ReactNode;
  isTabbed?: boolean;
};

const HeaderSection = ({ children, isTabbed, hasAdditionalContent }: SettingsPageWrapperHeaderProps) => {
  return isTabbed ? (
    <div className={classnames(style['tabbed-header-section'], { [style['has-additional-content']]: hasAdditionalContent })}>
      {children}
    </div>
  ) : (
    children
  );
};

export const SettingsPageHeader = ({ messages, descriptionContent, isTabbed = false }: SettingsPageHeaderProps) => {
  const showDescriptionSection = Boolean(messages.descriptionMessage || descriptionContent);
  const descriptionClassName = isTabbed ? undefined : style['description-section'];

  return (
    <HeaderSection hasAdditionalContent={showDescriptionSection} isTabbed={isTabbed}>
      <SettingsPageTitle formattedMessageValues={messages.headerMessage.values} value={messages.headerMessage} />
      {showDescriptionSection ? (
        <div className={descriptionClassName}>
          {messages.descriptionMessage ? (
            <Text
              formattedMessageValues={messages.descriptionMessage?.values}
              textType={TextType.Description}
              value={messages.descriptionMessage}
            />
          ) : null}
          {descriptionContent}
        </div>
      ) : null}
    </HeaderSection>
  );
};
